import React, { use } from 'react';
import classNames from 'classnames';
import isEqual from 'lodash/isEqual';

// downshift
import {
  DownshiftProps,
  GetToggleButtonPropsOptions,
  useMultipleSelection,
  useSelect,
  UseSelectState,
  UseSelectStateChangeOptions,
} from 'downshift';

// components
import Icon from '../icon';
import Button from '../button';
import EllipsisWithTooltip from '../ellipsis-with-tooltip';
import { Flex } from '../box';

// style
import { dropdownSizes, MultiSelectionComponentWrapper } from './style';

type Item = any;

export interface MultiSelectorProps {
  itemToString?: DownshiftProps<Item>['itemToString'];
  items: Item[];
  onChange?: (item: Item) => void;
  placeholder?: React.ReactNode;
  isItemDisabled?: (item: Item) => boolean;
  renderItem?: (item: Item) => React.JSX.Element;
  value?: Item[];
  isItemSelected?: (selectedItems?: Item[], item?: Item) => boolean;
  children: React.ReactNode;
  disabled?: boolean;
  onItemSelect?: (item: Item) => void;
  active?: boolean;
  dropdownSize?: keyof typeof dropdownSizes;
}

interface ContextValues {
  getToggleButtonProps: (options: GetToggleButtonPropsOptions) => any;
  selectedItems: Item[];
  isOpen?: boolean;
  placeholder?: React.ReactNode;
  disabled?: boolean;
  active?: boolean;
}

const MultiSelectContext = React.createContext<ContextValues>({
  isOpen: false,
  selectedItems: [],
  getToggleButtonProps: () => Object.assign({}),
  placeholder: '',
  disabled: false,
  active: false,
});

const MultiSelector: React.FC<MultiSelectorProps> & {
  Button: React.FC<MultiSelectorButtonProps>;
  DefaultButton: React.FC;
} = ({
  placeholder = '...',
  value,
  itemToString = item => {
    if (typeof item === 'string') {
      return item;
    }

    if (typeof item?.name === 'string' || React.isValidElement(item?.name)) {
      return item?.name;
    }

    return '';
  },
  items,
  onChange,
  isItemDisabled = () => false,
  renderItem,
  children,
  disabled,
  onItemSelect,
  active,
  isItemSelected = (selectedItems, item) => {
    return !!selectedItems?.find(selected => isEqual(selected, item));
  },
  dropdownSize,
}: MultiSelectorProps) => {
  /**
   * Multiple selection downshift logic
   * uses to handle the logic of multiple selection
   */

  const { selectedItems, setSelectedItems } = useMultipleSelection({
    selectedItems: value || [],
    onSelectedItemsChange: changes => {
      onChange?.(changes.selectedItems);
    },
  });

  /**
   * Select downshift logic
   */
  const stateReducer = (state: UseSelectState<Item>, actionAndChanges: UseSelectStateChangeOptions<Item>) => {
    const { type, changes } = actionAndChanges;
    switch (type) {
      // these cases are for keeping the menu open after clicking or selecting an item
      case useSelect.stateChangeTypes.ItemClick:
      case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter: {
        return {
          ...changes,
          isOpen: true,
          highlightedIndex: state.highlightedIndex,
        };
      }
      default:
        return changes;
    }
  };
  const { isOpen, getToggleButtonProps, getMenuProps, highlightedIndex, getItemProps } = useSelect({
    stateReducer,
    items,
    isItemDisabled: isItemDisabled,
    selectedItem: null,
    itemToString,
    onSelectedItemChange: changes => {
      if (isItemSelected(selectedItems, changes.selectedItem)) {
        setSelectedItems(selectedItems.filter(item => !isEqual(item, changes.selectedItem)));
      } else {
        setSelectedItems([...selectedItems, changes.selectedItem]);
      }

      onItemSelect?.(changes.selectedItem);
    },
  });

  /**
   * Render logic
   */

  const isDisabled = disabled || !items?.length;
  const itemsToRender = items;

  return (
    <MultiSelectContext
      value={{
        isOpen,
        selectedItems,
        placeholder,
        active,
        getToggleButtonProps: (options: GetToggleButtonPropsOptions) => {
          return getToggleButtonProps({
            disabled: isDisabled,
            ...options,
            className: classNames(options?.className, 'toggle-button'),
            onKeyDown: (e: React.KeyboardEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLDivElement>) => {
              // if the menu is open and escape is clicked , stop event propagation in case another element listens for it
              if (e.key === 'Escape' && isOpen) {
                e.stopPropagation();
              }
            },
          });
        },
        disabled: isDisabled,
      }}
    >
      <MultiSelectionComponentWrapper dropdownVariant={'default'}>
        {children}
        <ul
          {...getMenuProps({
            className: classNames('dropdown', { open: isOpen }, { [`size-${dropdownSize}`]: dropdownSize }),
          })}
        >
          {isOpen &&
            itemsToRender.map((itemData, itemIndex) => {
              const item = itemData;
              const isSelected = isItemSelected(selectedItems, item);
              const index = itemIndex;
              return (
                <li
                  key={`item ${index}`}
                  {...getItemProps({
                    item,
                    index,
                    'aria-selected': isSelected,
                  })}
                  className={classNames('menu-item', {
                    highlighted: highlightedIndex === index,
                  })}
                >
                  <div className={'inner-item-container align-items-center'}>
                    <Icon
                      className={'item-icon'}
                      name={isSelected ? 'CHECKBOX_SELECTED' : 'CHECKBOX'}
                      variant={isSelected ? 'action' : 'gray600'}
                      size={'xxl'}
                    />
                    {renderItem?.(item) || (
                      <div className={'item-text'}>
                        <EllipsisWithTooltip>{itemToString(item)}</EllipsisWithTooltip>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
        </ul>
      </MultiSelectionComponentWrapper>
    </MultiSelectContext>
  );
};

/**
 * Default button - this is the default UI button for our multi select component.
 * it uses context to get some valuable props from the root component.
 */

MultiSelector.DefaultButton = () => {
  return (
    <MultiSelector.Button>
      {({ selectedItems, disabled, active, isOpen, getToggleButtonProps, placeholder, ...rest }) => (
        <Button
          textAlign={'left'}
          variant={'secondary'}
          block
          size={'md'}
          weight={400}
          active={isOpen || active}
          justifyContent={'space-between'}
          disabled={disabled}
          {...getToggleButtonProps({})}
          {...rest}
        >
          <Flex maxWidth={'90%'} flexGrow={1}>
            {selectedItems.length ? (
              <React.Fragment>
                <div style={{ maxWidth: '90%' }}>
                  <EllipsisWithTooltip>{placeholder}</EllipsisWithTooltip>
                </div>
                <span>&nbsp;({selectedItems.length})</span>
              </React.Fragment>
            ) : (
              <EllipsisWithTooltip>{placeholder}</EllipsisWithTooltip>
            )}
          </Flex>
          <Flex justifyContent={'flex-end'} style={{ width: '10%' }}>
            <Icon className={'toggle-icon'} name={'CHEVRON_DOWN'} />
          </Flex>
        </Button>
      )}
    </MultiSelector.Button>
  );
};

/**
 *
 * This is a headless button component, which is a render props component.
 * It aims to render a button as children function and pass necessary props for them to use and determine they're look and feel
 */
interface MultiSelectorButtonProps {
  children: (props: ContextValues) => React.JSX.Element;
}

const MultiSelectorButton = ({ children }: MultiSelectorButtonProps) => {
  const { isOpen, selectedItems, getToggleButtonProps, placeholder, disabled, active }: ContextValues =
    use(MultiSelectContext);

  return children({ isOpen, selectedItems, getToggleButtonProps, placeholder, disabled, active });
};

MultiSelector.Button = MultiSelectorButton;

export default MultiSelector;
